import { FC, useEffect, useMemo } from "react";
import { useStyles } from "./Auth.styles";
import {
  Checkbox,
  Group,
  MantineProvider,
  Select,
  Tooltip,
} from "@mantine/core";
import {
  EMAIL_REGEX,
  InputGroup,
  NAME_REGEX,
} from "@funded-here-interface/common";

interface IProps {
  isEditable?: boolean;
  numOfDirectors: number;
  allDirectorsEmailPresent: boolean;
  directorChecked: boolean;
  setDirectorChecked: (value: boolean) => void;
  error: {
    repEmail: string;
    representative_first_name: string;
    representative_last_name: string;
    representative_title: string;
  };
  repEmail: string;
  directorEmails: string[];
  handleAuthDirectorEmailChange: (email: string) => void;
  setRepEmail: (value: string) => void;
  setRepresentativeFirstName: (value: string) => void;
  representativeFirstName: string;
  setRepresentativeLastName: (value: string) => void;
  representativeLastName: string;
  setRepresentativeTitle: (value: string) => void;
  representative_title: string;
}

interface InputFieldConfig {
  id: string;
  label: string;
  value: string;
  errorKey: keyof IProps["error"];
  setValue: (value: string) => void;
  regex: RegExp;
}

const Auth: FC<IProps> = (props) => {
  const { classes } = useStyles();
  const {
    numOfDirectors,
    allDirectorsEmailPresent,
    directorChecked,
    setDirectorChecked,
    error,
    repEmail,
    directorEmails,
    handleAuthDirectorEmailChange,
    setRepEmail,
    setRepresentativeFirstName,
    representativeFirstName,
    setRepresentativeLastName,
    representativeLastName,
    setRepresentativeTitle,
    representative_title,
    isEditable,
  } = props;

  useEffect(() => {
    if (directorChecked) {
      if (numOfDirectors < 1 || !allDirectorsEmailPresent) {
        // No directors available, uncheck and clear fields
        setDirectorChecked(false);
        setRepEmail("");
        setRepresentativeFirstName("");
        setRepresentativeLastName("");
        setRepresentativeTitle("");
      }
    }
  }, [numOfDirectors, allDirectorsEmailPresent, directorChecked]);

  // Input field configurations
  const inputFields = useMemo<InputFieldConfig[]>(
    () => [
      {
        id: "repEmail",
        label: "Email",
        value: repEmail,
        errorKey: "repEmail",
        setValue: setRepEmail,
        regex: EMAIL_REGEX,
      },
      {
        id: "representative_first_name",
        label:
          "First / given name (as shown in the national identification document)",
        value: representativeFirstName,
        errorKey: "representative_first_name",
        setValue: setRepresentativeFirstName,
        regex: NAME_REGEX,
      },
      {
        id: "representative_last_name",
        label: "Last name (as shown in the national identification document)",
        value: representativeLastName,
        errorKey: "representative_last_name",
        setValue: setRepresentativeLastName,
        regex: NAME_REGEX,
      },
      {
        id: "representative_title",
        label: "Role in the company",
        value: representative_title,
        errorKey: "representative_title",
        setValue: setRepresentativeTitle,
        regex: NAME_REGEX,
      },
    ],
    [
      repEmail,
      representativeFirstName,
      representativeLastName,
      representative_title,
      setRepEmail,
      setRepresentativeFirstName,
      setRepresentativeLastName,
      setRepresentativeTitle,
    ]
  );

  // Helper to handle input change with error clearing
  const handleInputChange = (field: InputFieldConfig, value: string) => {
    error[field.errorKey] = ""; // Clear error
    const processedValue =
      field.id === "repEmail"
        ? value.replace(field.regex, "").trim()
        : value.replace(field.regex, "");
    field.setValue(processedValue);
  };

  return (
    <>
      <Group style={{ width: "640px" }}>
        <p className={classes.authTitle}>Authorized Representative</p>
        <MantineProvider theme={{ cursorType: "pointer" }}>
          <Tooltip
            label="Requires at least 1 Director"
            disabled={numOfDirectors > 1 && allDirectorsEmailPresent}
          >
            <div>
              <Checkbox
                disabled={
                  numOfDirectors === 0 ||
                  !allDirectorsEmailPresent ||
                  !isEditable
                }
                checked={directorChecked}
                labelPosition="left"
                label="Is the authorized representative one of the above directors?"
                onChange={(event) =>
                  setDirectorChecked(event.currentTarget.checked)
                }
              />
            </div>
          </Tooltip>
        </MantineProvider>
      </Group>
      {directorChecked && (
        <Select
          disabled={!isEditable}
          error={error.repEmail}
          label="Email"
          value={repEmail}
          placeholder="Select Director"
          data={directorEmails}
          onChange={handleAuthDirectorEmailChange}
          styles={{ input: { height: "42px", marginTop: "3px" } }}
        />
      )}
      {!directorChecked && (
        <>
          {inputFields.map((field) => (
            <InputGroup
              key={field.id}
              isErrored={error[field.errorKey]}
              onChange={(value: string) => handleInputChange(field, value)}
              labelText={field.label}
              id={field.id}
              value={field.value}
              readOnly={!isEditable}
            />
          ))}
        </>
      )}
    </>
  );
};

export default Auth;
